import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, getHostels, markNotificationRead, markAllNotificationsRead } from '../api';
import { FiBell, FiCheck, FiCheckSquare, FiCoffee, FiEdit2, FiTrash2, FiLogOut, FiUserPlus, FiClipboard, FiSearch } from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';

const TYPE_META = {
  hostel_request: { label: 'Request', icon: FiClipboard, color: '#5b6785' },
  attendance_edit: { label: 'Attendance', icon: FiCheckSquare, color: '#c48a00' },
  meal_edit: { label: 'Meals', icon: FiCoffee, color: '#059669' },
  new_admission: { label: 'New Resident', icon: FiUserPlus, color: '#3b6fd4' },
  edit: { label: 'Edit', icon: FiEdit2, color: '#3b6fd4' },
  delete: { label: 'Delete', icon: FiTrash2, color: '#dc2626' },
  checkout: { label: 'Checkout', icon: FiLogOut, color: '#7c3aed' },
};

export default function Notifications() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('unread');
  const [hostelFilter, setHostelFilter] = useState('');
  const [search, setSearch] = useState('');
  const [hostels, setHostels] = useState([]);
  const limit = 20;

  useEffect(() => { getHostels().then(res => setHostels(res.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      if (hostelFilter) params.hostel_id = hostelFilter;
      if (search) params.search = search;
      const res = await getNotifications(params);
      setNotifications(res.data.notifications);
      setTotal(res.data.total);
    } catch (e) { /* non-fatal */ }
    setLoading(false);
  }, [page, statusFilter, hostelFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleRead = async (n) => {
    if (!n.is_read) {
      try { await markNotificationRead(n.id); load(); } catch (e) { /* non-fatal */ }
    }
    if (n.type === 'new_admission' && n.hostel_id) {
      navigate(`/hostel-admissions?hostel_id=${n.hostel_id}`);
    } else {
      navigate('/hostel-requests');
    }
  };

  const handleReadAll = async () => {
    try { await markAllNotificationsRead(); load(); } catch (e) { /* non-fatal */ }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiBell /> Notifications</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>Every request or record submitted by a hostel, in one place</p>
        </div>
        {can('notifications', 'edit') && <button className="btn btn-secondary btn-small" onClick={handleReadAll}><FiCheck /> Mark All Read</button>}
      </div>

      <div className="form-row" style={{ marginBottom: '16px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Status</label>
          <select className="form-control" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Hostel</label>
          <select className="form-control" value={hostelFilter} onChange={e => { setHostelFilter(e.target.value); setPage(1); }}>
            <option value="">All hostels</option>
            {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Search (student or hostel)</label>
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input className="form-control" style={{ paddingLeft: '30px' }} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search..." />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {notifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', background: '#f7f8fc', borderRadius: '10px' }}>
              No notifications found.
            </div>
          ) : notifications.map(n => {
            const meta = TYPE_META[n.type] || { label: n.type, icon: FiBell, color: '#5b6785' };
            const Icon = meta.icon;
            return (
              <div
                key={n.id}
                onClick={() => handleRead(n)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                  background: n.is_read ? '#fff' : '#fef8ec', border: `1px solid ${n.is_read ? 'var(--border-light)' : '#f6dfa6'}`,
                }}
              >
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: `${meta.color}18`, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={17} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '13.5px', color: 'var(--text-primary)' }}>{n.title}</strong>
                    {!n.is_read && <span className="badge badge-gold">New</span>}
                  </div>
                  {n.message && <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--text-secondary)' }}>{n.message}</p>}
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
                    {new Date(n.created_at).toLocaleString('en-PK')}
                    {n.is_read && n.read_by_name && ` · Read by ${n.read_by_name}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="pagination">
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Previous</button>
        <span>Page {page} of {totalPages} ({total} notifications)</span>
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
      </div>
    </div>
  );
}
